import { useState, useEffect } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { modificarDatosPersonales, UrlGet, UpdatePhone, UpdateEmail, UrlUpdate } from "./method";
import Swal from "sweetalert2";
import "../../assets/css/pin.css";
import Encryptor from "../../utils/Encryptor.js";

export default function Update() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        id: "",
        rut: "",
        phone: "",
        hasEmail: true,
        email: "",
        repitEmail: "",
        password: "",
        isEnrolled: false,
        link: "",
        image: "",
        updateMail: false,
        updatePhone: false,
    });

    const [oldPhone, setOldPhone] = useState();
    const [oldEmail, setOldEmail] = useState();

    // Validaciones
    const phoneIsInvalid = (telefono) => !/^\d{9}$/.test(telefono);
    const emailIsValid = (email) =>
        /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(email);

    // Validación del formulario
    const isFormValid = () => {
        const phoneValid = !phoneIsInvalid(data.phone);
        const emailValid = emailIsValid(data.email);
        const repitEmailValid = emailIsValid(data.repitEmail) && data.email === data.repitEmail;
        return phoneValid && emailValid && repitEmailValid;
    };

    // Obtener datos al cargar
    useEffect(() => {
        const fetchData = async () => {
            try {
                const crypto = new Encryptor();
                const encrypted = crypto.decrypt(id);

                const response = await UrlGet(encrypted);
                if (!response) {
                    navigate("/PageNotFound");
                } else {
                    console.log('Datos cargados desde backend:', response);
                    console.log('ID cargado:', response.id);
                    
                    // Si el teléfono tiene 8 dígitos, agregar el 9 al inicio
                    let phoneWithPrefix = response.phone;
                    if (response.phone && response.phone.length === 8) {
                        phoneWithPrefix = '9' + response.phone;
                    }
                    
                    setData({
                        ...response,
                        phone: phoneWithPrefix
                    });
                    setOldPhone(phoneWithPrefix);
                    setOldEmail(response.email);
                }
            } catch (error) {
                console.error("Error al obtener los datos:", error);
                navigate("/PageNotFound");
            }
        };

        fetchData();
    }, [id, navigate]);

    // Confirmar envío
    const handleConfirmData = async (e) => {
        e.preventDefault();

        if (!isFormValid()) {
            Swal.fire("Error", "Por favor, complete todos los campos correctamente.", "error");
            return;
        }

        setLoading(true);

        try {            
            const phoneChanged = oldPhone !== data.phone;
            const emailChanged = oldEmail !== data.email;

            if (!phoneChanged && !emailChanged) {
                const result = await Swal.fire({
                    title: "Sin cambios detectados",
                    icon: "info",
                    text: "No se detectaron cambios en tus datos de contacto. ¿Deseas continuar de todos modos?",
                    showCancelButton: true,
                    confirmButtonText: "Sí, continuar",
                    cancelButtonText: "Cancelar",
                    allowOutsideClick: false,
                    customClass: {
                        confirmButton: "btn btn-success",
                        cancelButton: "btn btn-danger",
                    },
                    reverseButtons: true,
                });

                if (result.isConfirmed) {
                    // Agregar delay para asegurar sincronización de datos
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                    // Navegar a la siguiente página
                    navigate(`/pin/send/${id}`);
                }

                return; // Salimos para evitar enviar datos innecesariamente
            }

            // Guardar los nuevos datos temporalmente (sin modificar en .NET aún)
            // Los datos se modificarán en .NET solo después de validar el PIN
            try {
                console.log('Guardando datos temporalmente antes de validación PIN:', data);
                
                // Actualizar datos en el almacenamiento temporal
                await UrlUpdate({
                    ...data,
                    pendingUpdate: true // Marcar que hay una actualización pendiente
                });
                
                // Actualizar flags según el estado actual del formulario
                if (data.phone && !phoneIsInvalid(data.phone)) {
                    await UpdatePhone(data.id, true);
                    console.log('UpdatePhone: true (teléfono válido presente)');
                }

                if (data.email && data.email.trim() !== "") {
                    await UpdateEmail(data.id, true);
                    console.log('UpdateEmail: true (email activo y válido)');
                }

                // Navegar a validación de PIN (los datos se guardarán en .NET después de validar)
                navigate(`/pin/send/${id}`);
                
            } catch (error) {
                console.error('Error al guardar datos temporalmente:', error);
                Swal.fire("Error", "Hubo un problema al procesar los datos.", "error");
            }
        } catch (error) {
            console.error("Error al confirmar datos:", error);
            Swal.fire("Error", "Hubo un problema al guardar los datos.", "error");
        } finally {
            setLoading(false);
        }
    };

    // Manejo de cambios
    const handleInputChange = (field, value) => {
        setData((prevData) => ({ ...prevData, [field]: value }));
    };

    // Ir atrás
    const handleGoBack = () => {
        navigate("/paet/datosAfiliados");
    };

    return (
        <div className="container">
            <div className="card formulario pin mt-5 mb-5">
                <div className="card-header">
                    <div className="form-row">
                        <div className="titulo">
                            Formulario de Retiro de Pensión Anticipada por Enfermedad Terminal
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="subtitulo mt-3">Confirmación datos de contacto</div>
                    </div>
                    <div className="form-row">
                        <div className="subtitulo-2 mt-3">
                            Estos datos serán los utilizados para notificarte sobre el avance de tu solicitud.
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    <Form onSubmit={handleConfirmData}>
                        {/* Teléfono */}
                        <Form.Group>
                            <Form.Label>
                                <strong className="asterisco">*</strong> Teléfono
                            </Form.Label>
                            <Form.Control
                                type="tel"
                                maxLength={9}
                                placeholder="912345678"
                                value={data.phone}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    if (value.length <= 9) handleInputChange("phone", value);
                                }}
                                isInvalid={phoneIsInvalid(data.phone)}
                            />
                            <Form.Control.Feedback type="invalid">
                                Por favor, ingrese un teléfono válido (9 dígitos).
                            </Form.Control.Feedback>
                        </Form.Group>

                        {/* Checkbox Email eliminado: el email siempre es requerido */}

                        {/* Campos Email (siempre visibles) */}
                        <>
                                <Form.Group className="mt-3">
                                    <Form.Label>
                                        <strong className="asterisco">*</strong> Correo
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        maxLength={50}
                                        placeholder="correo@example.com"
                                        value={data.email}
                                        onChange={(e) => handleInputChange("email", e.target.value.trim())}
                                        isInvalid={!emailIsValid(data.email)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Por favor, ingrese un correo válido.
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mt-3">
                                    <Form.Label>
                                        <strong className="asterisco">*</strong> Confirmar correo
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        maxLength={50}
                                        placeholder="correo@example.com"
                                        value={data.repitEmail}
                                        onChange={(e) => handleInputChange("repitEmail", e.target.value.trim())}
                                        isInvalid={
                                            data.email !== data.repitEmail || !emailIsValid(data.repitEmail)
                                        }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {data.repitEmail === ""
                                            ? "Por favor, confirma el correo."
                                            : data.email !== data.repitEmail
                                                ? "Los correos no coinciden."
                                                : "Correo inválido."}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </>

                        {/* Botones */}
                        <div className="mt-4 text-center">
                            <Button
                                variant="success"
                                type="submit"
                                disabled={!isFormValid() || loading}
                                className="col-md-12"
                            >
                                {loading ? (
                                    <Spinner animation="border" variant="light" size="sm" />
                                ) : (
                                    "Confirmar datos"
                                )}
                            </Button>

                            <Button
                                variant="link"
                                onClick={handleGoBack}
                                className="col-md-12 mt-2 d-none"
                            >
                                Volver al paso anterior
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
}
